import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Logos } from "@/components/Logos";
import { api, apiError, setToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const INPUT =
  "mt-2 h-12 rounded-none border-zinc-800 bg-zinc-900/60 px-4 font-data text-zinc-100 placeholder:text-zinc-600 focus-visible:border-[color:var(--acc-focus)] focus-visible:ring-1 focus-visible:ring-[color:var(--acc-focus)] focus-visible:ring-offset-0";
const LABEL = "font-data text-[10px] uppercase tracking-[0.2em] text-zinc-500";
const SUBMIT =
  "flex h-12 w-full items-center justify-center gap-2 rounded-none bg-[color:var(--acc-mid)] font-display text-sm font-bold uppercase tracking-[0.18em] text-white transition-colors duration-200 hover:bg-[color:var(--acc-deep)] disabled:cursor-not-allowed disabled:opacity-40";

export default function SetPassword() {
  const [pw, setPw] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const { user, refresh } = useAuth();
  const navigate = useNavigate();

  const tooShort = pw.length > 0 && pw.length < 8;
  const mismatch = again.length > 0 && pw !== again;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { new_password: pw });
      setToken(data.token);
      await refresh();
      toast.success("Your password is set. Please use it from now on.");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hero-grid flex min-h-screen items-center justify-center bg-zinc-950 px-6 py-16">
      <div className="w-full max-w-[420px]" data-testid="set-password-page">
        <div className="mb-10 inline-flex items-center gap-3 border border-white/10 bg-white/[0.04] p-2 backdrop-blur-md">
          <Logos boxed size="h-10" />
        </div>
        <div className="font-data text-[10px] uppercase tracking-[0.24em] text-[color:var(--acc-bright)]">
          First sign-in · set your password
        </div>
        <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-zinc-50">
          Choose your own password
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-zinc-400">
          You signed in with the password the portal gave you
          {user?.email ? ` (${user.email.split("@")[0]})` : ""}. Please replace it before you go in —
          at least 8 characters, and not the one you have just used.
        </p>

        <form onSubmit={submit} className="mt-9 space-y-5">
          <div>
            <label className={LABEL} htmlFor="new-pw">
              Your new password
            </label>
            <Input
              id="new-pw"
              type="password"
              required
              autoFocus
              data-testid="new-password-input"
              placeholder="At least 8 characters"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className={INPUT}
            />
            {tooShort && (
              <p className="mt-2 text-xs text-[#F59E0B]" data-testid="password-too-short">
                A little longer, please — at least 8 characters.
              </p>
            )}
          </div>
          <div>
            <label className={LABEL} htmlFor="again-pw">
              Type it once more
            </label>
            <Input
              id="again-pw"
              type="password"
              required
              data-testid="confirm-password-input"
              value={again}
              onChange={(e) => setAgain(e.target.value)}
              className={INPUT}
            />
            {mismatch && (
              <p className="mt-2 text-xs text-[#F59E0B]" data-testid="password-mismatch">
                The two do not match.
              </p>
            )}
          </div>
          <button
            type="submit"
            disabled={busy || pw.length < 8 || pw !== again}
            data-testid="save-password-button"
            className={SUBMIT}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save and enter"}
          </button>
        </form>

        <div className="mt-8 flex items-start gap-2 border border-zinc-800 bg-zinc-900/60 p-3 text-xs text-zinc-400">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--acc-bright)]" />
          <span>
            Nobody at SDTD can see your password. If you forget it, ask an administrator to reset it —
            or sign in with a 6-digit passcode instead.
          </span>
        </div>
        <div className="mt-6 flex items-center gap-2 font-data text-[10px] uppercase tracking-[0.16em] text-zinc-600">
          <KeyRound className="h-3 w-3" /> Both ways in stay open
        </div>
      </div>
    </div>
  );
}
